import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { filter, firstValueFrom, Subject, switchMap, takeUntil } from "rxjs";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { ConfirmationDialogComponent } from "@bitwarden/web-vault/app/components/confirmation-dialog/confirmation-dialog.component";
import {
  BillAlert,
  PushNotificationService,
} from "@bitwarden/web-vault/app/services/notification/push-notification.service";
import { BillAlertDialogComponent } from "./bill-alert-dialog.component";
import { mobileSlideInConfig } from "@bitwarden/web-vault/app/components/dialog/mobile-slide-in-dialog";

@Component({
  selector: "app-notifications",
  templateUrl: "notifications.component.html",
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private i18nService = inject(I18nService);
  private platformUtilsService = inject(PlatformUtilsService);
  private dialog = inject(MatDialog);
  protected pushNotificationService = inject(PushNotificationService);

  private destroy$ = new Subject<void>();

  protected addAlertButton = new ActionButton({
    text: this.i18nService.t("addBillAlert"),
    class: "primary",
    isEnabled: true,
    icon: "plus",
    onClick: () => this.openAddDialog(),
  });

  protected enableNotificationsButton = new ActionButton({
    text: this.i18nService.t("enableNotifications"),
    class: "primary",
    isEnabled: true,
    onClick: () => this.requestPermission(),
  });

  async ngOnInit() {
    await this.pushNotificationService.initialize();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async requestPermission() {
    const granted = await this.pushNotificationService.requestPermission();
    if (!granted) {
      this.platformUtilsService.showToast(
        "warning",
        null,
        this.i18nService.t("pushNotificationsPermissionDenied"),
      );
    }
  }

  openAddDialog() {
    const ref = this.dialog.open(BillAlertDialogComponent, {
      ...mobileSlideInConfig("no-background-dialog"),
      width: "540px",
      maxWidth: "100vw",
    });

    ref
      .afterClosed()
      .pipe(
        filter((result): result is Omit<BillAlert, "id"> => !!result),
        switchMap((result) => this.pushNotificationService.saveBillAlert(result)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.platformUtilsService.showToast("success", null, this.i18nService.t("billAlertSaved"));
      });
  }

  async deleteAlert(alert: BillAlert) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: "confirmation-dialog",
      disableClose: true,
      data: {
        title: "delete",
        type: "warning",
        message: this.i18nService.t("deleteBillAlertConfirmation", alert.payee),
        customizedButton: {
          yes: { text: "delete", class: "danger" },
          no: { text: "cancel", class: "neutral" },
        },
      },
    });
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }
    await this.pushNotificationService.deleteBillAlert(alert.id);
    this.platformUtilsService.showToast("success", null, this.i18nService.t("billAlertDeleted"));
  }

  formatDueDate(isoDate: string): string {
    const [year, month, day] = isoDate.split("T")[0].split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  }

  getPaymentTypeLabel(type: string): string {
    const key = `paymentType${type.charAt(0).toUpperCase()}${type.slice(1)}`;
    return this.i18nService.t(key) || type;
  }
}
